import { useEffect, useRef } from 'react'
import L from 'leaflet'
import type { Position } from '../../../../types'
import { FOLLOW_THRESHOLD, FOLLOW_TIMEOUT, DEFAULT_ZOOM } from '../constants'

interface UseMapFollowProps {
  mapRef: React.MutableRefObject<L.Map | null>
  isInitializedRef: React.MutableRefObject<boolean>
  position: Position | null
  follow: boolean
  isTracking: boolean
  userHasInteractedRef: React.MutableRefObject<boolean>
  isUserDraggingRef: React.MutableRefObject<boolean>
  isSnapshotLockedRef: React.MutableRefObject<boolean>
  saveMapSnapshot: () => void
  setIsLoadingTiles: (loading: boolean) => void
}

export const useMapFollow = ({
  mapRef,
  isInitializedRef,
  position,
  follow,
  isTracking,
  userHasInteractedRef,
  isUserDraggingRef,
  isSnapshotLockedRef,
  saveMapSnapshot,
  setIsLoadingTiles,
}: UseMapFollowProps) => {
  const hasSetInitialPositionRef = useRef(false)
  const lastFollowPositionRef = useRef<{ lat: number; lon: number } | null>(null)
  const followTimeoutRef = useRef<number | null>(null)

  // Reset initial position tracking when tracking starts/stops
  useEffect(() => {
    if (isTracking) {
      // When tracking starts, reset flags and force map to GPS position immediately
      hasSetInitialPositionRef.current = false
      userHasInteractedRef.current = false
      lastFollowPositionRef.current = null
      isSnapshotLockedRef.current = false // Unlock snapshot to allow movement
      
      // If we have a position, move to it immediately
      if (position && mapRef.current && isInitializedRef.current) {
        try {
          mapRef.current.setView([position.lat, position.lon], DEFAULT_ZOOM, {
            animate: true,
            duration: 0.8,
          })
          lastFollowPositionRef.current = { lat: position.lat, lon: position.lon }
        } catch (error) {
          console.warn('Could not set initial tracking position:', error)
        }
      }
    } else {
      hasSetInitialPositionRef.current = false
      userHasInteractedRef.current = false
      lastFollowPositionRef.current = null
    }
  }, [isTracking, userHasInteractedRef, position, mapRef, isInitializedRef, isSnapshotLockedRef])

  // Handle position updates and follow mode
  useEffect(() => {
    if (!mapRef.current || !isInitializedRef.current || !position) return

    try {
      const container = mapRef.current.getContainer()
      if (!container || !container.offsetParent || container.offsetWidth === 0) {
        return
      }
    } catch (error) {
      return
    }

    const { lat, lon } = position
    const ll: [number, number] = [lat, lon]

    // Check for significant position change
    if (lastFollowPositionRef.current) {
      const distance = L.latLng(lastFollowPositionRef.current.lat, lastFollowPositionRef.current.lon).distanceTo(ll)
      if (distance > 50) {
        userHasInteractedRef.current = false
        lastFollowPositionRef.current = null
      }
    }

    // Follow mode logic
    if (follow && !userHasInteractedRef.current && !isUserDraggingRef.current && !isSnapshotLockedRef.current) {
      const container = mapRef.current.getContainer()
      if (!container || container.offsetWidth === 0 || container.offsetHeight === 0) {
        return
      }

      if (followTimeoutRef.current) {
        clearTimeout(followTimeoutRef.current)
      }

      followTimeoutRef.current = window.setTimeout(() => {
        if (!mapRef.current || !follow || !isInitializedRef.current || userHasInteractedRef.current || isUserDraggingRef.current) return

        try {
          const map = mapRef.current
          if (!map.getContainer() || !map.getContainer().offsetParent) {
            return
          }

          let currentCenter
          try {
            currentCenter = map.getCenter()
          } catch (error) {
            return
          }

          if (!currentCenter) return

          const lastFollowPos = lastFollowPositionRef.current
          if (lastFollowPos) {
            const distanceFromLastFollow = L.latLng(lastFollowPos.lat, lastFollowPos.lon).distanceTo(L.latLng(lat, lon))
            if (distanceFromLastFollow <= FOLLOW_THRESHOLD) {
              return
            }
          }

          const distance = currentCenter.distanceTo(L.latLng(lat, lon))

          if (distance > FOLLOW_THRESHOLD) {
            try {
              const currentZoom = map.getZoom()
              map.setView(ll, currentZoom, {
                animate: true,
                duration: 0.8,
              })
              lastFollowPositionRef.current = { lat, lon }
              setTimeout(() => {
                saveMapSnapshot()
              }, 1000)
            } catch (error) {
              console.warn('Could not update follow mode view:', error)
            }
          }
        } catch (error) {
          console.warn('Error in follow mode:', error)
        }
      }, FOLLOW_TIMEOUT)
    }

    // Set initial position when first position arrives
    if (!hasSetInitialPositionRef.current && position && mapRef.current && isInitializedRef.current) {
      const currentPosition = position
      hasSetInitialPositionRef.current = true

      let savedSnapshot: { center: [number, number]; zoom: number } | null = null
      try {
        const saved = sessionStorage.getItem('mapSnapshot')
        if (saved) {
          const parsed = JSON.parse(saved)
          if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
            savedSnapshot = { center: parsed.center, zoom: parsed.zoom }
          }
        }
      } catch (e) {
        // Ignore storage errors
      }

      userHasInteractedRef.current = false
      lastFollowPositionRef.current = null

      const timeoutId = window.setTimeout(() => {
        if (mapRef.current && currentPosition) {
          try {
            const currentCenter = mapRef.current.getCenter()
            const isDefaultPosition = currentCenter &&
              Math.abs(currentCenter.lat - 52.37) < 0.1 &&
              Math.abs(currentCenter.lng - 4.9) < 0.1

            if (savedSnapshot && !userHasInteractedRef.current && !isTracking) {
              mapRef.current.setView(savedSnapshot.center, savedSnapshot.zoom, {
                animate: false,
              })
              isSnapshotLockedRef.current = true
            } else if (!currentCenter || isDefaultPosition ||
              (Math.abs(currentCenter.lat - currentPosition.lat) > 0.01 ||
                Math.abs(currentCenter.lng - currentPosition.lon) > 0.01)) {
              mapRef.current.setView([currentPosition.lat, currentPosition.lon], DEFAULT_ZOOM, {
                animate: true,
                duration: 0.5,
              })

              lastFollowPositionRef.current = { lat: currentPosition.lat, lon: currentPosition.lon }

              setTimeout(() => {
                saveMapSnapshot()
              }, 1000)
            }
          } catch (error) {
            console.warn('Could not set initial position:', error)
          }
        }
      }, 300)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [position?.lat, position?.lon, follow, isTracking, mapRef, isInitializedRef, userHasInteractedRef, isUserDraggingRef, isSnapshotLockedRef, saveMapSnapshot, setIsLoadingTiles])

  useEffect(() => {
    return () => {
      if (followTimeoutRef.current) {
        clearTimeout(followTimeoutRef.current)
      }
    }
  }, [])

  return {
    hasSetInitialPositionRef,
    lastFollowPositionRef,
    followTimeoutRef,
  }
}
